// Der Rahmen des CRM am Laptop – nach dem Mockup (mockup/js/app.js,
// `rahmenLaptop`): links die schwarze Seitenleiste mit Logo, Firma, der
// Navigation in Gruppen und unten dem Angemeldeten mit „abmelden“; rechts
// der Kopf der Seite (großer Titel, Zeile darunter, rechts ihre Knöpfe) und
// der Inhalt. Das Test-Band bleibt ganz oben stehen, auch beim Scrollen –
// damit niemand Spieldaten mit dem echten Stand verwechselt.
//
// Was in der Leiste steht, rechnet navigation.ts aus der Rolle (Kern,
// `darf`). Bis 1000 px Breite steht die Leiste immer da (am Laptop meist
// 1280 px, kleinere bis 1024 px); darunter wird sie ein Menü hinter dem
// Knopf oben links.
//
// Seit S6 (Gernots Entscheidung 2: kein Web-Push, offene Fragen „oben,
// sichtbar, nicht zu übersehen“): Der Rahmen holt die offenen Fragen
// (fragen/offene.tsx) – daraus die Zahl am Eintrag „Fragen“ und das Band in
// Lime oben auf jeder Seite, unter dem Kopf. Für jede Rolle gleich, auch
// für die Montage.
//
// Seit E3c, S2 (O69): Jede Minute fragt der Rahmen nur noch die Zahlen
// (GET /api/crm/zaehler – „zu tun“ und die offenen Fragen mit ihrem Stand),
// nicht mehr die ganzen Listen. Die Liste der offenen Fragen holt er erst
// neu, wenn sich ihr Stand ändert (oder nach einer eigenen Aktion).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { darf, ROLLEN_NAMEN, type ZaehlerAntwort } from "@happy-energy/kern";
import { holen, Ikon, TestBand } from "@happy-energy/oberflaeche";
import { initialen } from "./anzeige.ts";
import { OffeneFragenBand } from "./fragen/FragenBand.tsx";
import { NACHFRAGEN_MS, OffeneFragenKontext, useOffeneFragenHolen } from "./fragen/offene.tsx";
import { STUFE_TEXT } from "./kisten.ts";
import { aktiverPfad, navigation, type NavEintrag } from "./navigation.ts";
import { useAmServerGeaendert } from "./speicher.ts";
import { useCrm } from "./zusammenhang.tsx";

/** Das Menü am schmalen Bildschirm: der Knopf im Kopf öffnet es. */
const MenueKontext = createContext<() => void>(() => undefined);

/** „MO elektro GmbH“ → „MO“; „Sonnenstrom KG“ → „SO“. */
function firmenKuerzel(name: string): string {
  const erstes = name.trim().split(/\s+/)[0] ?? "";
  return (erstes.length <= 3 ? erstes : erstes.slice(0, 2)).toUpperCase() || "?";
}


function imHintergrund(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * Die Zahlen der Seitenleiste (seit E3c, S2 – O69): „zu tun“ und die offenen
 * Fragen samt ihrem Stand, aus EINEM kleinen Weg. Fragt beim Seitenwechsel,
 * jede Minute (nur, solange der Tab vorne ist – was fällig war, holt er nach,
 * sobald er wieder vorne ist) und gleich nach einer eigenen Aktion
 * („Freigeben“, „gesehen“, eine Frage …, auch im anderen Fenster). Geht es
 * nicht, bleiben die letzten Zahlen stehen.
 */
function useZaehler(an: boolean, pfad: string): ZaehlerAntwort | null {
  const [zahlen, setZahlen] = useState<ZaehlerAntwort | null>(null);
  const [takt, setTakt] = useState(0);
  /** Fällig gewesen, während der Tab im Hintergrund war. */
  const verpasst = useRef(false);
  const neu = useCallback(() => setTakt((n) => n + 1), []);
  useAmServerGeaendert(neu);
  useEffect(() => {
    if (!an) return;
    const uhr = window.setInterval(neu, NACHFRAGEN_MS);
    const sichtbar = () => {
      if (!imHintergrund() && verpasst.current) {
        verpasst.current = false;
        neu();
      }
    };
    document.addEventListener("visibilitychange", sichtbar);
    return () => {
      window.clearInterval(uhr);
      document.removeEventListener("visibilitychange", sichtbar);
    };
  }, [an, neu]);
  useEffect(() => {
    if (!an) return;
    if (imHintergrund()) {
      verpasst.current = true;
      return;
    }
    let gilt = true;
    holen<ZaehlerAntwort>("/api/crm/zaehler").then(
      (z) => {
        if (gilt) setZahlen(z);
      },
      () => undefined,
    );
    return () => {
      gilt = false;
    };
  }, [an, pfad, takt]);
  return an ? zahlen : null;
}

/** Die Zahlen an den Einträgen der Leiste – null: (noch) keine. */
type Zaehler = Record<NonNullable<NavEintrag["zaehler"]>, number | null>;

/** Was die Zahl am Eintrag heißt – für Bildschirmleser. */
function zaehlerWort(art: NonNullable<NavEintrag["zaehler"]>, n: number): string {
  if (art === "fragen") return n === 1 ? "1 offene Frage" : `${n} offene Fragen`;
  return n === 1 ? "bei 1 Anlage ist etwas zu tun" : `bei ${n} Anlagen ist etwas zu tun`;
}

function Eintrag({ e, aktiv, zaehler, gewaehlt }: { e: NavEintrag; aktiv: boolean; zaehler: Zaehler; gewaehlt: () => void }) {
  const zahl = e.zaehler ? zaehler[e.zaehler] : null;
  return (
    <a href={`#${e.pfad}`} className={aktiv ? "aktiv" : undefined} aria-current={aktiv ? "page" : undefined} onClick={gewaehlt}>
      <Ikon name={e.ikon} />
      <span className="crm-nav-wort">{e.name}</span>
      <span className="rechts">
        {e.zaehler && zahl !== null && zahl > 0 && (
          <span className="zaehler" aria-label={zaehlerWort(e.zaehler, zahl)}>
            {zahl}
          </span>
        )}
        {e.punkt && <i className={`nav-punkt ${e.punkt}`} title={STUFE_TEXT[e.punkt]} />}
      </span>
    </a>
  );
}

/** Der ganze Rahmen: Test-Band, Seitenleiste, rechts die Seite (`children` – meist eine <Seite>). */
export function Rahmen({ pfad, children }: { pfad: string; children: ReactNode }) {
  const { ich, hilfen } = useCrm();
  const rolle = ich.nutzer.rolle;
  const gruppen = useMemo(() => navigation(rolle), [rolle]);
  const aktiv = aktiverPfad(gruppen, pfad, rolle);
  // Steht ein Pfad zweimal in der Leiste (beim Verkauf führen Sales/Doku und
  // „Meine Besichtigungen“ beide zu #/pruefen), wird nur der erste hell.
  const hell = useMemo(() => {
    for (const g of gruppen) {
      const i = g.eintraege.findIndex((e) => e.pfad === aktiv);
      if (i >= 0) return `${g.titel}|${i}`;
    }
    return null;
  }, [gruppen, aktiv]);
  const zahlen = useZaehler(darf(rolle, "fragen"), pfad);
  const offeneFragen = useOffeneFragenHolen(darf(rolle, "fragen"), zahlen?.fragenStand ?? null);
  // Die Zahl der Fragen aus der Liste, sobald sie da ist – so sagen Band und Zahl immer dasselbe.
  const zaehler: Zaehler = { "zu-tun": zahlen?.zuTun ?? null, fragen: offeneFragen.fragen?.length ?? zahlen?.offeneFragen ?? null };

  // Das Menü (schmaler Bildschirm) gilt für die Seite, auf der es geöffnet wurde – ein Klick auf einen Eintrag schließt es.
  const [offenAuf, setOffenAuf] = useState<string | null>(null);
  const offen = offenAuf === pfad;
  const auf = useCallback(() => setOffenAuf(pfad), [pfad]);
  const zu = useCallback(() => setOffenAuf(null), []);
  useEffect(() => {
    if (!offen) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") zu();
    };
    document.addEventListener("keydown", taste);
    return () => document.removeEventListener("keydown", taste);
  }, [offen, zu]);

  return (
    <MenueKontext.Provider value={auf}>
      <OffeneFragenKontext.Provider value={offeneFragen}>
        <div className={`crm-huelle${ich.umgebung === "echt" ? "" : " mit-band"}`}>
          <TestBand umgebung={ich.umgebung} />
          {hilfen.ohneVerbindung && (
            <div className="crm-ohne-verbindung" role="status">
              <Ikon name="offline" groesse="k" /> Keine Verbindung zum Server – sobald sie zurück ist, geht es von selbst weiter.
            </div>
          )}
          <div className="crm">
            <div className={`crm-schleier${offen ? " an" : ""}`} onClick={zu} aria-hidden="true" />
            <aside className={`crm-seite${offen ? " offen" : ""}`} aria-label="Navigation">
              <button type="button" className="crm-zu" aria-label="Menü schließen" onClick={zu}>
                <Ikon name="kreuz" />
              </button>
              <a className="crm-logo" href="#/" aria-label="Happy Energy CRM – zur Startseite">
                <img src="/logo-horizontal.png" alt="Happy Energy" />
              </a>
              <div className="crm-firma">
                <span className="avatar avatar-dunkel">{firmenKuerzel(ich.firma.name)}</span>
                <div>
                  <b>{ich.firma.name}</b>
                  <span>Happy Energy CRM</span>
                </div>
              </div>
              {gruppen.map((g) => (
                <div key={g.titel}>
                  <div className="crm-gruppe-titel">{g.titel}</div>
                  <nav className="crm-nav" aria-label={g.titel}>
                    {g.eintraege.map((e, i) => (
                      <Eintrag key={`${g.titel}-${e.pfad}-${e.name}`} e={e} aktiv={hell === `${g.titel}|${i}`} zaehler={zaehler} gewaehlt={zu} />
                    ))}
                  </nav>
                  {g.legende && (
                    <div className="punkt-legende" aria-label="Was die Punkte heißen">
                      <span>
                        <i className="nav-punkt s1" /> {STUFE_TEXT.s1}
                      </span>
                      <span>
                        <i className="nav-punkt teil" /> {STUFE_TEXT.teil}
                      </span>
                      <span>
                        <i className="nav-punkt spaeter" /> {STUFE_TEXT.spaeter}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              <div className="crm-nutzer">
                <span className="avatar avatar-tuerkis">{initialen(ich.nutzer.name)}</span>
                <div className="crm-nutzer-wort">
                  <b>{ich.nutzer.name}</b>
                  <span>{ROLLEN_NAMEN[rolle]}</span>
                </div>
                <button type="button" className="crm-abmelden" onClick={hilfen.abmelden}>
                  abmelden
                </button>
              </div>
            </aside>
            <main className="crm-haupt">{children}</main>
          </div>
        </div>
      </OffeneFragenKontext.Provider>
    </MenueKontext.Provider>
  );
}

/**
 * Eine Seite im Rahmen: der Kopf (Titel, Zeile darunter, rechts die Knöpfe
 * – ohne Knöpfe der Platzhalter „Suchen“) und darunter der Inhalt. Der
 * Titel steht auch im Reiter des Browsers.
 *
 * Ganz oben im Inhalt das Band „offene Fragen“ (seit S6). `fragenHier`: die
 * Besichtigungen, deren Fragen die Seite selbst zeigt (Freigabe, Akte) – das
 * Band nennt dann nur die übrigen. `ohneFragenBand`: die Seite „Fragen“.
 */
export function Seite({
  titel,
  unter,
  knoepfe,
  fragenHier,
  ohneFragenBand = false,
  children,
}: {
  titel: string;
  unter?: ReactNode;
  knoepfe?: ReactNode;
  fragenHier?: readonly string[];
  ohneFragenBand?: boolean;
  children: ReactNode;
}) {
  const menueAuf = useContext(MenueKontext);
  const kopf = useRef<HTMLElement>(null);

  useEffect(() => {
    document.title = `${titel} · Happy Energy CRM`;
  }, [titel]);

  // Wie hoch der Kopf ist – die Material-Spalte klebt beim Scrollen darunter (crm.css, --crm-kopf).
  useEffect(() => {
    const el = kopf.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const messen = () => document.documentElement.style.setProperty("--crm-kopf", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);

  return (
    <>
      <header className="crm-kopf" ref={kopf}>
        <button type="button" className="crm-menue-knopf" aria-label="Menü öffnen" onClick={menueAuf}>
          <Ikon name="menue" />
        </button>
        <div className="crm-kopf-wort">
          <h1>{titel}</h1>
          {unter && <div className="unter">{unter}</div>}
        </div>
        <div className="crm-kopf-rechts">{knoepfe ?? <SucheSpaeter />}</div>
      </header>
      <div className="crm-inhalt">
        {!ohneFragenBand && <OffeneFragenBand {...(fragenHier ? { hier: fragenHier } : {})} />}
        {children}
      </div>
    </>
  );
}

/** Zu einer Seite des CRM wechseln (Adresse hinter dem #) – wie ein Klick auf einen Link, der Zurück-Knopf geht mit. */
export function geheZu(pfad: string): void {
  window.location.assign(`#${pfad}`);
}

/** Der Platz für die Suche – gestrichelt, sie kommt in Stufe 2 (wie im Mockup). */
export function SucheSpaeter() {
  return (
    <span className="suche" title="Suchen kommt in Stufe 2">
      <Ikon name="suche" groesse="k" /> Suchen – kommt in Stufe 2
    </span>
  );
}
